import { useState } from 'react';

import { TBaseOption } from '@r1-frontend/ui-react/components/costs/PurchaseOptions/types';
import { SingleArrowDown, SingleArrowUp } from '@r1-frontend/ui-react/components/svg/arrows';
import ControlElement from '@r1-frontend/ui-react/components/typography/controlElement';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import RouterCard from '@r1-frontend/ui-react/organisms/Card/Router';
import { usePartialList } from '@r1-frontend/shared/hooks/usePartialList';

import AddRouterPopup from '~/src/entities/speed-bonus/content/pages/routers/components/AddRouterPopup';
import { decrementQuantity, incrementQuantity } from '~/src/entities/speed-bonus/context/actions';
import { IPreparedRouter } from '~/src/entities/speed-bonus/context/actions/setRouters';
import { addRouter } from '~/src/entities/speed-bonus/context/actions/thunk';
import { useContextDispatch, useContextSelector } from '~/src/entities/speed-bonus/context/hooks';
import { selectRouters } from '~/src/entities/speed-bonus/context/selectors';
import { TRouter } from '~/src/entities/speed-bonus/context/selectors/selectRouters';
import { getPurchaseOptions } from '~/src/entities/speed-bonus/helpers';
import { useValueByBreakpoint } from '~/src/helpers/hooks/useValueByBreakpoint';

const RoutersList = (): JSX.Element => {
    const dispatch = useContextDispatch();

    const routers = useContextSelector(selectRouters);

    const cardCount = useValueByBreakpoint({
        desktop: 3,
        sDesktop: 2,
    });

    const [partialRouters, isPartial, setIsPartial] = usePartialList(routers, cardCount);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [currentRouter, setCurrentRouter] = useState<{ value: IPreparedRouter, options: Record<number, TBaseOption> }>();

    const openAddEquipmentPopup = (equipment: TRouter, isInstallmentDisable: boolean) => {
        setCurrentRouter({
            value: equipment,
            options: getPurchaseOptions(equipment.prices, (isInstallmentDisable && equipment.isInstalmentLimit)),
        });

        setIsPopupOpen(true);
    };

    const handleAddEquipment = (cost: number) => {
        setIsPopupOpen(false);
        if (currentRouter?.value) {
            dispatch(addRouter(currentRouter.value, cost));
        }
    };

    const handleIncrement = (id: number) => {
        dispatch(incrementQuantity(id));
    };

    const handleDecrement = (id: number) => {
        dispatch(decrementQuantity(id));
    };

    const handleClosePopup = () => {
        setIsPopupOpen(false);
    };

    const onToggleList = () => {
        if (!isPartial) {
            window.scrollTo(0, 0);
        }

        setIsPartial(!isPartial);
    };

    return (
        <FullWidthListContainer $size={16} $direction={'column'} $wrap={'nowrap'}>
            <FullWidthListContainer $desktop={16} $mobile={12} $direction={'row'} $wrap={'wrap'}>
                {partialRouters.map(router => (
                    <RouterCard
                        key={router.id}
                        images={router.images}
                        title={router.title}
                        description={router.categoryTitle}
                        characteristics={router.characteristics}
                        labels={router.labelElements}
                        cost={router.selectedPrice.cost}
                        period={router.selectedPrice.duration}
                        quantity={router.quantity}
                        disabled={router.disabled}
                        onButtonClick={() => openAddEquipmentPopup(router, true)}
                        onIncrement={() => handleIncrement(router.id)}
                        onDecrement={() => handleDecrement(router.id)}
                        onCostClick={() => openAddEquipmentPopup(router, false)}
                        btnText={'Добавить'}
                    />
                ))}
            </FullWidthListContainer>

            {routers.length > cardCount && (
                <ControlElement
                    Icon={isPartial ? SingleArrowDown : SingleArrowUp}
                    onClick={onToggleList}
                >
                    {isPartial ? 'Показать все роутеры' : 'Скрыть роутеры'}
                </ControlElement>
            )}

            {currentRouter && (
                <AddRouterPopup
                    isPopupOpen={isPopupOpen}
                    title={currentRouter.value.title}
                    images={currentRouter.value.images}
                    routerProps={currentRouter.value.props}
                    routerOptions={currentRouter.options}
                    onClose={handleClosePopup}
                    onSelect={handleAddEquipment}
                />
            )}
        </FullWidthListContainer>
    );
};

export default RoutersList;
